import Phaser from 'phaser';
import { COLORS } from '../style.js';

/**
 * Icons are drawn relative to (0, 0) — the caller positions the Graphics
 * object. Color is a 0xRRGGBB int, alpha is 0..1. Each icon's effective
 * bounding box is roughly 28×28.
 */
export type IconDrawFn = (
  g: Phaser.GameObjects.Graphics,
  color: number,
  alpha: number
) => void;

/**
 * NavBarItem is a discriminated union — each slot ships either a
 * Graphics-drawn icon (the original vector approach) or a preloaded
 * PNG (white-on-transparent, recolored via setTint at runtime).
 *
 * Mixing both in the same NavBar is fine — NavBar handles each kind
 * separately during the active/inactive recolor cycle.
 */
export type NavBarItem =
  | { readonly id: string; readonly draw: IconDrawFn }
  | { readonly id: string; readonly imageKey: string };

interface NavSlot {
  readonly id: string;
  readonly indicator: Phaser.GameObjects.Rectangle;
  readonly hit: Phaser.GameObjects.Rectangle;
  readonly visual:
    | {
        readonly kind: 'graphics';
        readonly icon: Phaser.GameObjects.Graphics;
        readonly draw: IconDrawFn;
      }
    | {
        readonly kind: 'image';
        readonly icon: Phaser.GameObjects.Image;
      };
}

const INDICATOR_WIDTH = 36;
const INDICATOR_HEIGHT = 3;
const ACTIVE_ALPHA = 1.0;
const INACTIVE_ALPHA = 0.85;

// Icons draw on a ~28×28 design canvas; this multiplier scales the
// whole Graphics object up so the rendered nav icons feel chunkier on
// real devices without needing to rewrite every draw function's coords.
const ICON_SCALE = 1.5;

// Visible bounding box for image-based icons. Matches the Graphics
// icons' rendered extent: 28 design units × ICON_SCALE = 42 visible px.
// PNG sources are 500×500 — setDisplaySize handles the downscale.
const IMAGE_DISPLAY_SIZE = 28 * ICON_SCALE;

/**
 * App-level bottom navigation. Pure presentation — exposes setActiveIndex
 * for callers to drive selection programmatically and onSelect for tap.
 * The active slot gets a cyan indicator bar across its top edge plus a
 * cyan-tinted icon; inactive slots use dim steel.
 */
export class NavBar {
  private readonly slots: NavSlot[] = [];
  private activeIndex: number;
  private readonly onSelect: (id: string) => void;
  private readonly allowActiveTap: boolean;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    height: number,
    items: readonly NavBarItem[],
    activeIndex: number,
    onSelect: (id: string) => void,
    allowActiveTap: boolean = false
  ) {
    this.activeIndex = activeIndex;
    this.onSelect = onSelect;
    this.allowActiveTap = allowActiveTap;

    // Background — same panel surface as the action panel, separated by
    // the 1px cyan accent line at the top edge.
    scene.add.rectangle(
      x + width / 2,
      y + height / 2,
      width,
      height,
      COLORS.bgPanelInt
    );
    const accent = scene.add.graphics();
    accent.lineStyle(1, COLORS.accentCyanInt, 0.8);
    accent.lineBetween(x, y, x + width, y);

    const slotW = width / items.length;
    items.forEach((item, i) => {
      const cx = x + slotW * i + slotW / 2;
      const cy = y + height / 2;

      // Hit area — invisible rect covering the slot.
      const hit = scene.add.rectangle(cx, cy, slotW, height, 0, 0);
      hit.setInteractive({ cursor: 'pointer' });

      // Active indicator — small horizontal bar at top edge of the slot.
      const indicator = scene.add.rectangle(
        cx,
        y + INDICATOR_HEIGHT / 2 + 1,
        INDICATOR_WIDTH,
        INDICATOR_HEIGHT,
        COLORS.accentCyanInt
      );
      indicator.setVisible(i === activeIndex);

      // Icon — either Graphics (vector draw fn) or Image (preloaded
      // white-on-transparent PNG, tinted on selection change).
      let slot: NavSlot;
      if ('imageKey' in item) {
        const icon = scene.add.image(cx, cy + 2, item.imageKey);
        icon.setDisplaySize(IMAGE_DISPLAY_SIZE, IMAGE_DISPLAY_SIZE);
        slot = {
          id: item.id,
          indicator,
          hit,
          visual: { kind: 'image', icon },
        };
      } else {
        const icon = scene.add.graphics();
        icon.x = cx;
        icon.y = cy + 2;
        icon.setScale(ICON_SCALE);
        slot = {
          id: item.id,
          indicator,
          hit,
          visual: { kind: 'graphics', icon, draw: item.draw },
        };
      }
      this.redrawSlot(slot, i === activeIndex);
      this.slots.push(slot);

      hit.on('pointerdown', () => this.handleTap(i));
    });
  }

  setActiveIndex(i: number): void {
    if (i < 0 || i >= this.slots.length) return;
    if (i === this.activeIndex) return;
    const prev = this.slots[this.activeIndex];
    const next = this.slots[i];
    this.activeIndex = i;
    if (prev) {
      prev.indicator.setVisible(false);
      this.redrawSlot(prev, false);
    }
    if (next) {
      next.indicator.setVisible(true);
      this.redrawSlot(next, true);
    }
  }

  private handleTap(index: number): void {
    // Active tap is a no-op for the canonical scene of each section
    // (Home / Inbox / Settings). Sub-scenes (a specific game, a
    // specific lobby) pass `allowActiveTap` so re-tapping the
    // highlighted section icon routes back to its canonical scene.
    if (index === this.activeIndex) {
      if (!this.allowActiveTap) return;
      const slot = this.slots[index];
      if (slot) this.onSelect(slot.id);
      return;
    }
    this.setActiveIndex(index);
    const slot = this.slots[index];
    if (slot) this.onSelect(slot.id);
  }

  private redrawSlot(slot: NavSlot, active: boolean): void {
    const color = active
      ? COLORS.navIconActiveInt
      : COLORS.navIconInactiveInt;
    const alpha = active ? ACTIVE_ALPHA : INACTIVE_ALPHA;
    if (slot.visual.kind === 'graphics') {
      slot.visual.icon.clear();
      slot.visual.draw(slot.visual.icon, color, alpha);
    } else {
      // White-on-transparent PNG — setTint multiplies the RGB so
      // white -> tint color cleanly.
      slot.visual.icon.setTint(color);
      slot.visual.icon.setAlpha(alpha);
    }
  }
}

// --- Icon draw functions. All draw centered on (0, 0) in their Graphics'
// local coords, with effective extent ~±14px. Stroke-only line work for
// the tactical aesthetic.

export const drawHomeIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  // House silhouette as one closed path.
  g.beginPath();
  g.moveTo(-13, 0);
  g.lineTo(0, -13);
  g.lineTo(13, 0);
  g.lineTo(13, 12);
  g.lineTo(-13, 12);
  g.closePath();
  g.strokePath();
  // Door
  g.strokeRect(-4, 4, 8, 8);
};

/**
 * Crossed-swords icon for the "games" slot in the AppNav. Two
 * diagonal blades form the X; short perpendicular crossguards near
 * each hilt are what distinguish "crossed swords" from a plain X.
 * Blade tips at the top corners; hilts at the bottom corners.
 */
export const drawGamesIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  // Blades.
  g.lineBetween(-13, -13, 13, 13);
  g.lineBetween(13, -13, -13, 13);
  // Crossguards — short perpendicular bars at ~75% along each blade
  // from the tip, sitting in the lower quadrants so the eye reads
  // hilts-down. Positions computed as: blade-midpoint ± a 4-unit
  // perpendicular offset.
  g.lineBetween(4, 11, 11, 4);   // bottom-right (sword from top-left)
  g.lineBetween(-11, 4, -4, 11); // bottom-left (sword from top-right)
};

export const drawChatIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  g.strokeRoundedRect(-14, -10, 28, 16, 4);
  // Tail pointing down-left.
  g.beginPath();
  g.moveTo(-4, 6);
  g.lineTo(-9, 12);
  g.lineTo(2, 6);
  g.closePath();
  g.strokePath();
};

/**
 * Two-figure handshake silhouette for the Friends tab. Two stick
 * figures in a row (head + torso + splayed legs) with their outer
 * arms hanging at their sides and their inner arms reaching down
 * and across to clasp at the center — the universal handshake
 * gesture.
 *
 * Earlier iteration had the inner arms meeting overhead in a V
 * (intended as a high-five) but the upward V read more like
 * jumping-jacks than a friends gesture.
 */
export const drawFriendsIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  // ---- Left figure (cx = -9) ----
  g.strokeCircle(-9, -8, 3);            // head
  g.lineBetween(-9, -5, -9, 6);         // torso
  g.lineBetween(-9, -3, -12, 5);        // outer (left) arm hanging down-out
  g.lineBetween(-9, 6, -12, 13);        // outer leg
  g.lineBetween(-9, 6, -6, 13);         // inner leg
  // ---- Right figure (cx = 9) ----
  g.strokeCircle(9, -8, 3);
  g.lineBetween(9, -5, 9, 6);
  g.lineBetween(9, -3, 12, 5);          // outer (right) arm
  g.lineBetween(9, 6, 12, 13);
  g.lineBetween(9, 6, 6, 13);
  // ---- Inner arms reaching down to clasp at center (handshake) ----
  // Forms a downward V between the figures, meeting at the chest /
  // waist level so the arms read as outstretched toward each other.
  g.lineBetween(-9, -3, 0, 3);
  g.lineBetween(9, -3, 0, 3);
};

export const drawTrophyIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  // Cup body — narrow trapezoid.
  g.beginPath();
  g.moveTo(-9, -10);
  g.lineTo(9, -10);
  g.lineTo(7, 4);
  g.lineTo(-7, 4);
  g.closePath();
  g.strokePath();
  // Stem
  g.lineBetween(0, 4, 0, 9);
  // Base
  g.lineBetween(-5, 9, 5, 9);
  g.lineBetween(-9, 12, 9, 12);
  g.lineBetween(-5, 9, -9, 12);
  g.lineBetween(5, 9, 9, 12);
};

export const drawProfileIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  // Head
  g.strokeCircle(0, -6, 5);
  // Shoulders — bottom half of an ellipse, drawn as a polyline.
  const rx = 11;
  const ry = 8;
  const cy = 4;
  g.beginPath();
  g.moveTo(-rx, cy);
  const segments = 14;
  for (let i = 1; i <= segments; i++) {
    const t = (i / segments) * Math.PI;
    g.lineTo(-rx * Math.cos(t), cy + ry * Math.sin(t));
  }
  g.strokePath();
};

/**
 * Gear / cog icon — the settings glyph in the AppNav. Eight short
 * radial teeth around the body of the gear, a hollow axle in the
 * middle. Drawn at the same 2px stroke + ~28px bounding box as the
 * other AppNav icons so they sit on a consistent visual baseline.
 */
export const drawSettingsIcon: IconDrawFn = (g, color, alpha) => {
  g.lineStyle(3, color, alpha);
  // Gear body
  g.strokeCircle(0, 0, 8);
  // Axle hole
  g.strokeCircle(0, 0, 2.5);
  // 8 teeth — short radial ticks at 45° intervals around the body.
  const teethCount = 8;
  const innerR = 8.5;
  const outerR = 12;
  for (let i = 0; i < teethCount; i++) {
    const angle = (i / teethCount) * Math.PI * 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    g.lineBetween(cos * innerR, sin * innerR, cos * outerR, sin * outerR);
  }
};
